import redX from "../../images/redX2.svg";
import toast from "react-hot-toast";
import classes from "./AddVehicle.module.css";
import placeholderImg from "../../images/placeholderImg.svg";
import { apiUrl } from "../../constants";
import { BackBtn } from "../../components/buttons/backBtn/BackBtn";
import { ActionBtn } from "../../components/buttons/actionBtn/ActionBtn";
import { PageWrapper } from "../../components/UI/pageWrapper/PageWrapper";
import { InputElement } from "../../components/inputElement/InputElement";
import { fetchDrivers } from "../../redux/driver/thunks/getDriverThunk";
import { SelectElement } from "../../components/selectElement/SelectElement";
import { LoadingSpinner } from "../../components/loadingSpinner/LoadingSpinner";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { useStatusEffect } from "../../helperFunctions/customHooks/useStatusEffect";
import { useEffect, useRef } from "react";
import { validateRequiredFields } from "../../helperFunctions/validateRequiredFields";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsEditMode,
  setSelectedDataForEdit,
} from "../../redux/globalFunctions/globalFnSlice";
import {
  createVehicle,
  resetCreateVehicleState,
} from "../../redux/vehicle/createVehicle";
import {
  resetUpdateVehicleState,
  updateVehicle,
} from "../../redux/vehicle/updateVehicle";
import {
  resetVehicleForm,
  setVehicleForm,
} from "../../redux/forms/vehicleForm";

export function AddVehicle() {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { t } = useTranslations();
  const text = t.addVehicle;
  const validationText = t.formValidations;
  const { status: createStatus, error: createError } = useSelector(
    (state) => state.createVehicle
  );
  const { status: updateStatus, error: updateError } = useSelector(
    (state) => state.updateVehicle
  );
  const { drivers } = useSelector((state) => state.drivers);
  const { isEditMode, selectedDataForEdit } = useSelector(
    (state) => state.global
  );

  const isStatusLoading =
    createStatus === "loading" || updateStatus === "loading";

  const name = selectedDataForEdit?.v_name + " " + selectedDataForEdit?.v_model;

  const formData = useSelector((state) => state.forms.vehicleForm);

  // Initial fetch of data
  useEffect(() => {
    dispatch(fetchDrivers({ per_page: 100 }));
  }, []);

  useStatusEffect({
    status: createStatus,
    errorMessage: text.errorCreating + " " + createError,
    successMessage: text.successCreating,
    navigateLocation: "/vehicles",
    resetStatus: resetCreateVehicleState,
    resetForm: resetVehicleForm,
    resetSelectedDataForEdit: true,
  });

  useStatusEffect({
    status: updateStatus,
    errorMessage: text.errorUpdating + " " + updateError,
    successMessage: text.successUpdating,
    navigateLocation: "/vehicles",
    resetStatus: resetUpdateVehicleState,
    resetForm: resetVehicleForm,
    resetSelectedDataForEdit: true,
    resetEditMode: true,
  });

  // Populate fields when edit mode is true and there is data present.
  useEffect(() => {
    if (selectedDataForEdit && isEditMode) {
      const {
        v_name,
        v_model,
        v_plate_number,
        v_width,
        v_height,
        v_depth,
        v_trunk_capacity,
        v_driver_id,
        images,
      } = selectedDataForEdit;

      dispatch(
        setVehicleForm({
          name: v_name,
          model: v_model,
          plateNumber: v_plate_number,
          driver: v_driver_id,
          width: v_width,
          height: v_height,
          depth: v_depth,
          trunkSize: v_trunk_capacity,
          image: images ? images : [],
        })
      );
    }
  }, [selectedDataForEdit, isEditMode]);

  // Update the trunk size if the needed data is present.
  useEffect(() => {
    const { height, width, depth } = formData;

    // helper to accept "0,12" too
    const norm = (v) => parseFloat(String(v).replace(",", "."));

    if (width && height && depth) {
      const h = norm(height);
      const w = norm(width);
      const d = norm(depth);

      if ([h, w, d].every((num) => !isNaN(num) && num > 0)) {
        const volumeM3 = h * w * d; // m³
        const volumeCm3 = Math.round(volumeM3 * 1_000_000); // cm³ integer
        dispatch(setVehicleForm({ trunkSize: volumeCm3?.toFixed(2) }));
      }
    } else if (
      width === "" ||
      height === "" ||
      depth === "" ||
      width === 0 ||
      height === 0 ||
      depth === 0
    ) {
      dispatch(setVehicleForm({ trunkSize: "" }));
    }
  }, [formData.width, formData.depth, formData.height]);

  // Simulate click on the input:file.
  const handleClick = () => {
    fileInputRef.current.click();
  };

  // Update the form data.
  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(setVehicleForm({ [name]: value }));
  };

  // Image upload.
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

    dispatch(setVehicleForm({ image: [base64] }));

    event.target.value = null;
  };

  function handleRemoveImg() {
    dispatch(setVehicleForm({ image: [] }));
  }

  function onBackClick() {
    dispatch(setIsEditMode(false));
    dispatch(setSelectedDataForEdit(null));
    if (isEditMode) {
      dispatch(resetVehicleForm());
    }
  }

  const backBtn = [<BackBtn to={"/vehicles"} onClick={onBackClick} />];

  const driversWithFullName = drivers?.map((driver) => ({
    ...driver,
    fullName: `${driver.u_firstname} ${driver.u_lastname}`,
  }));

  async function urlToBase64(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  async function handleSubmit() {
    const requiredFields = [
      "name",
      "model",
      "plateNumber",
      "width",
      "height",
      "depth",
    ];

    // Validate required fields
    const errors = validateRequiredFields(
      formData,
      requiredFields,
      validationText
    );

    if (errors.length > 0) {
      toast.dismiss();
      setTimeout(() => {
        errors.forEach((err) =>
          toast.error(err, {
            duration: 2000,
          })
        );
      }, 100);
      return;
    }

    let imageBase64 = formData.image;

    // If image is from backend (URL path), convert to base64
    if (
      Array.isArray(formData.image) &&
      formData.image[0] &&
      !formData.image[0].startsWith("data:")
    ) {
      try {
        const fullUrl = `${apiUrl.replace(/\/+$/, "")}/${formData.image[0]
          .replace(/^\/+/, "")
          .replace(/\/{2,}/g, "/")}`;
        const base64 = await urlToBase64(fullUrl);
        imageBase64 = [base64];
      } catch (err) {
        console.error("Failed to convert image to base64:", err);
      }
    } else if (
      typeof formData.image === "string" &&
      !formData.image.startsWith("data:")
    ) {
      try {
        const fullUrl = `${apiUrl.replace(/\/+$/, "")}/${formData.image
          .replace(/^\/+/, "")
          .replace(/\/{2,}/g, "/")}`;
        const base64 = await urlToBase64(fullUrl);
        imageBase64 = [base64];
      } catch (err) {
        console.error("Failed to convert image to base64:", err);
      }
    }

    const finalData = {
      ...formData,
      image: imageBase64,
    };

    if (isEditMode) {
      dispatch(
        updateVehicle({ vehicleId: selectedDataForEdit.v_id, ...finalData })
      );
    } else {
      dispatch(createVehicle(finalData));
    }
  }

  function handleResetForm() {
    dispatch(resetVehicleForm());
  }

  const resetButton = [
    <ActionBtn
      text={t.addDeliveryModal.reset}
      onClick={handleResetForm}
      disabled={isEditMode}
      width={"110px"}
    />,
  ];

  return (
    <PageWrapper
      title={isEditMode ? `${text.editVehicle} ${name}` : text.addVehicle}
      backButton={backBtn}
      buttons={!isEditMode ? resetButton : []}
    >
      <div className={`${classes.content} fadeInTranslate`}>
        <form className={classes.form}>
          <section className={classes.carDetails}>
            <InputElement
              labelText={text.name}
              id={"name"}
              name={"name"}
              value={formData.name}
              onChange={handleChange}
            />
            <InputElement
              labelText={text.model}
              id={"model"}
              name={"model"}
              value={formData.model}
              onChange={handleChange}
            />
            <InputElement
              labelText={text.plateNumber}
              id={"plateNumber"}
              name={"plateNumber"}
              value={formData.plateNumber}
              onChange={handleChange}
            />
            <SelectElement
              id="driver"
              name="driver"
              value={formData.driver}
              onChange={handleChange}
              placeholder={text.driver}
              options={driversWithFullName || []}
              optionValueKey="u_id"
              optionLabelKey="fullName"
              width={"300px"}
            />
          </section>

          <section className={classes.dimensions}>
            <h3>{text.trunkDimensions}</h3>
            <div className={classes.dimensionElements}>
              <InputElement
                labelText={text.width}
                id={"width"}
                name={"width"}
                value={formData.width}
                onChange={handleChange}
                minLength={1}
                type={"number"}
              />
              <InputElement
                labelText={text.height}
                id={"height"}
                name={"height"}
                value={formData.height}
                onChange={handleChange}
                minLength={1}
                type={"number"}
              />
              <InputElement
                labelText={text.depth}
                id={"depth"}
                name={"depth"}
                value={formData.depth}
                onChange={handleChange}
                minLength={1}
                type={"number"}
              />
              <InputElement
                labelText={text.trunkSize}
                id={"trunkSize"}
                name={"trunkSize"}
                value={formData.trunkSize}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </section>

          <section className={classes.uploadImg}>
            <h3>{text.uploadImg}</h3>
            <div className={classes.imgContent}>
              <div className={classes.images}>
                {formData.image && formData.image.length !== 0 ? (
                  <div className={classes.photoContainer}>
                    <img
                      src={
                        Array.isArray(formData.image)
                          ? formData.image[0].startsWith("data:")
                            ? formData.image[0]
                            : `${apiUrl.replace(/\/+$/, "")}/${formData.image[0]
                                .replace(/^\/+/, "")
                                .replace(/\/{2,}/g, "/")}`
                          : formData.image.startsWith("data:")
                          ? formData.image
                          : `${apiUrl.replace(/\/+$/, "")}/${formData.image
                              .replace(/^\/+/, "")
                              .replace(/\/{2,}/g, "/")}`
                      }
                      alt="preview"
                      className={classes.image}
                    />
                    <img
                      src={redX}
                      alt="remove"
                      className={classes.redX}
                      onClick={handleRemoveImg}
                    />
                  </div>
                ) : (
                  <img
                    src={placeholderImg}
                    alt=""
                    onClick={handleClick}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </div>
              <div className={classes.chooseFile}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <button type="button" onClick={handleClick}>
                  {text.chooseFile}
                </button>
                {!formData.image && <p>{text.noFileChoosen}</p>}
              </div>
            </div>
          </section>
        </form>

        <div className={classes.submitBtnContainer}>
          <ActionBtn
            text={isStatusLoading ? <LoadingSpinner /> : text.save}
            height={"42px"}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </PageWrapper>
  );
}
